/**
 * Status utilities for status items
 */

export type StatusItemStatus =
  | "Open"
  | "Processed"
  | "Rework Required"
  | "Quality Reviewed"
  | "Partner Reviewed"
  | string;

/**
 * Normalize status values to display names
 */
export const normalizeStatusItemStatus = (status?: string): string => {
  if (!status) return "Open";

  const normalized = status.toLowerCase().trim();

  // Map internal values to display values
  const statusMap: Record<string, string> = {
    open: "Open",
    "not started": "Open",
    processed: "Processed",
    "in progress": "Processed",
    "rework required": "Rework Required",
    "rework_required": "Rework Required",
    "ready for review": "Rework Required",
    "quality reviewed": "Quality Reviewed",
    "quality_reviewed": "Quality Reviewed",
    "manager reviewed": "Quality Reviewed",
    "manager_reviewed": "Quality Reviewed",
    reviewed: "Quality Reviewed",
    "partner reviewed": "Partner Reviewed",
    "partner_reviewed": "Partner Reviewed",
    completed: "Partner Reviewed",
  };

  return statusMap[normalized] || status;
};

/**
 * Get the primary color for a status item status
 *
 * Color palette (Tailwind CSS references):
 * - Open:            #6B7280 (Gray 500)     - Neutral, professional gray
 * - Processed:       #3B82F6 (Blue 500)     - Corporate blue, work in progress
 * - Rework Required: #F59E0B (Amber 500)    - Bright warning orange-yellow
 * - Quality Reviewed:#A855F7 (Purple 500)   - Vibrant purple/pink
 * - Partner Reviewed:#22C55E (Green 500)    - Success green
 */
export const getStatusItemColor = (status?: string): string => {
  const normalized = normalizeStatusItemStatus(status).toLowerCase();

  if (normalized.includes("open") || normalized.includes("not started")) {
    return "#5b6e7f"; // BDO Slate 2.0
  } else if (normalized.includes("processed") || normalized.includes("in progress")) {
    return "#008fd2"; // BDO Ocean 2.0
  } else if (normalized.includes("rework")) {
    return "#98002e"; // BDO Burgundy
  } else if (normalized.includes("quality reviewed")) {
    return "#d67900"; // BDO Gold 2.0
  } else if (normalized.includes("partner reviewed")) {
    return "#009966"; // BDO Jade
  }

  return "#6B7280"; // Default gray
};

/**
 * Get the text color for a status (dark for light backgrounds, white for dark backgrounds)
 */
export const getStatusItemTextColor = (status?: string): string => {
  const normalized = normalizeStatusItemStatus(status).toLowerCase();
  return "#ffffff";
};

/**
 * Get a lighter version of the status color (for backgrounds)
 *
 * Light variants (Tailwind CSS 100 shades):
 * - Open:            #F3F4F6 (Gray 100)
 * - Processed:       #DBEAFE (Blue 100)
 * - Rework Required: #FEF3C7 (Amber 100)
 * - Quality Reviewed:#F3E8FF (Purple 100)
 * - Partner Reviewed:#DCFCE7 (Green 100)
 */
export const getStatusItemColorLight = (status?: string): string => {
  const normalized = normalizeStatusItemStatus(status).toLowerCase();

  if (normalized.includes("open") || normalized.includes("not started")) {
    return "#F3F4F6"; // Tailwind Gray 100
  } else if (normalized.includes("processed") || normalized.includes("in progress")) {
    return "#DBEAFE"; // Tailwind Blue 100
  } else if (normalized.includes("rework")) {
    return "#FEF3C7"; // Tailwind Amber 100
  } else if (normalized.includes("quality reviewed")) {
    return "#F3E8FF"; // Tailwind Purple 100
  } else if (normalized.includes("partner reviewed")) {
    return "#DCFCE7"; // Tailwind Green 100
  }

  return "#F3F4F6"; // Default Gray 100
};

/**
 * Get a darker version of the status color (for text/borders)
 *
 * Dark variants (Tailwind CSS 700 shades):
 * - Open:            #374151 (Gray 700)
 * - Processed:       #1D4ED8 (Blue 700)
 * - Rework Required: #B45309 (Amber 700)
 * - Quality Reviewed:#7E22CE (Purple 700)
 * - Partner Reviewed:#15803D (Green 700)
 */
export const getStatusItemColorDark = (status?: string): string => {
  const normalized = normalizeStatusItemStatus(status).toLowerCase();

  if (normalized.includes("open") || normalized.includes("not started")) {
    return "#374151"; // Tailwind Gray 700
  } else if (normalized.includes("processed") || normalized.includes("in progress")) {
    return "#1D4ED8"; // Tailwind Blue 700
  } else if (normalized.includes("rework")) {
    return "#B45309"; // Tailwind Amber 700
  } else if (normalized.includes("quality reviewed")) {
    return "#7E22CE"; // Tailwind Purple 700
  } else if (normalized.includes("partner reviewed")) {
    return "#15803D"; // Tailwind Green 700
  }

  return "#374151"; // Default Gray 700
};

/**
 * Get the background color with opacity for status badges
 */
export const getStatusItemColorBackground = (status?: string, opacity: number = 0.1): string => {
  const color = getStatusItemColor(status);
  // Convert hex to rgba
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Get status icon color (same as primary but can be customized separately)
 */
export const getStatusItemIconColor = (status?: string): string => {
  return getStatusItemColor(status);
};

/**
 * Status priority order (lower number = earlier in workflow = lower priority)
 * Used to determine which status should be displayed when a worksheet has multiple status items
 */
const STATUS_PRIORITY: Record<string, number> = {
  "Open": 0,
  "Rework Required": 1,
  "Processed": 2,
  "Quality Reviewed": 3,
  "Partner Reviewed": 4,
};

/**
 * Get the priority of a status (lower = earlier in workflow)
 */
export const getStatusPriority = (status?: string): number => {
  const normalized = normalizeStatusItemStatus(status);
  return STATUS_PRIORITY[normalized] ?? 0;
};

/**
 * Get the lowest status from a list of statuses
 * Returns the status that is earliest in the workflow
 */
export const getLowestStatus = (statuses: (string | undefined)[]): string => {
  if (statuses.length === 0) return "Open";

  let lowestStatus = "Partner Reviewed"; // Start with highest
  let lowestPriority = STATUS_PRIORITY["Partner Reviewed"];

  for (const status of statuses) {
    const normalized = normalizeStatusItemStatus(status);
    const priority = STATUS_PRIORITY[normalized] ?? 0;
    if (priority < lowestPriority) {
      lowestPriority = priority;
      lowestStatus = normalized;
    }
  }

  return lowestStatus;
};
